/**
 * Convert a saved Brand Voice Blueprint (9-section schema) to Markdown.
 * Used by the Tudástár "Letöltés Markdown" action — produces a plain-text
 * file the user can paste into Notion / Obsidian / GitHub README.
 */

const MD_VIBE_HEADER = "| Skála | Érték | Magyarázat |\n|---|---|---|";

function escapeMd(s) {
  if (s === null || s === undefined) return "";
  return String(s).replace(/\|/g, "\\|");
}

function listToCsv(arr) {
  if (!Array.isArray(arr)) return "";
  return arr.filter((x) => x !== null && x !== undefined && String(x).trim()).join(", ");
}

function bulletList(arr, { strike = false } = {}) {
  if (!Array.isArray(arr) || arr.length === 0) return "";
  return arr
    .filter((x) => x !== null && x !== undefined && String(x).trim())
    .map((x) => (strike ? `- ~~${x}~~` : `- ${x}`))
    .join("\n");
}

export function brandVoiceBlueprintToMarkdown(bp, name) {
  if (!bp) return "";
  const lines = [];
  lines.push(`# ${name || "Márkahang Blueprint"}`);
  lines.push("");

  if (bp.egy_mondatos_definicio) {
    lines.push("## EGY MONDATBAN");
    lines.push(bp.egy_mondatos_definicio);
    lines.push("");
  }

  const ess = bp.esszencia || {};
  if (ess.cim || ess.szoveg) {
    lines.push("## A TE HANGOD");
    if (ess.cim) lines.push(`**${ess.cim}**`);
    if (ess.szoveg) {
      lines.push("");
      lines.push(ess.szoveg);
    }
    lines.push("");
  }

  const arch = bp.archetipus || {};
  if (arch.fo || arch.masodlagos) {
    lines.push("## ARCHETÍPUS");
    if (arch.fo?.nev) {
      lines.push(`### Fő archetípus: ${arch.fo.nev}`);
      if (arch.fo.leiras) lines.push(arch.fo.leiras);
      lines.push("");
    }
    if (arch.masodlagos?.nev) {
      lines.push(`### Másodlagos: ${arch.masodlagos.nev}`);
      if (arch.masodlagos.leiras) lines.push(arch.masodlagos.leiras);
      lines.push("");
    }
  }

  const kivk = bp.ki_vagyok_ki_nem_vagyok || {};
  if (
    (Array.isArray(kivk.ki_vagyok) && kivk.ki_vagyok.length) ||
    (Array.isArray(kivk.ki_nem_vagyok) && kivk.ki_nem_vagyok.length)
  ) {
    lines.push("## KI VAGYOK / KI NEM VAGYOK");
    if (Array.isArray(kivk.ki_vagyok) && kivk.ki_vagyok.length) {
      lines.push("### Ki vagyok");
      lines.push(bulletList(kivk.ki_vagyok));
      lines.push("");
    }
    if (Array.isArray(kivk.ki_nem_vagyok) && kivk.ki_nem_vagyok.length) {
      lines.push("### Ki nem vagyok");
      lines.push(bulletList(kivk.ki_nem_vagyok, { strike: true }));
      lines.push("");
    }
  }

  if (Array.isArray(bp.hangnem) && bp.hangnem.length) {
    lines.push("## HANGNEM");
    lines.push(listToCsv(bp.hangnem));
    lines.push("");
  }

  if (Array.isArray(bp.vibe_skala) && bp.vibe_skala.length) {
    lines.push("## VIBE SKÁLA");
    lines.push(MD_VIBE_HEADER);
    bp.vibe_skala.forEach((v) => {
      const ertek = v.ertek ?? v.value ?? "";
      lines.push(`| ${escapeMd(v.nev || v.name || "")} | ${ertek} | ${escapeMd(v.magyarazat || "")} |`);
    });
    lines.push("");
  }

  const sf = bp.stiluslenyomat;
  if (sf && typeof sf === "object") {
    lines.push("## STÍLUSLENYOMAT");
    lines.push("_Az írásmintád alapján._");
    lines.push("");
    if (Array.isArray(sf.jellegzetes_szavak) && sf.jellegzetes_szavak.length) {
      lines.push("### Jellegzetes szóhasználat");
      lines.push(listToCsv(sf.jellegzetes_szavak));
      lines.push("");
    }
    if (Array.isArray(sf.mondatkezdesek) && sf.mondatkezdesek.length) {
      lines.push("### Saját mondatkezdéseid");
      sf.mondatkezdesek.forEach((m) => lines.push(`- _${m}_`));
      lines.push("");
    }
    if (sf.mondatritmus) {
      lines.push("### Mondatritmus");
      lines.push(sf.mondatritmus);
      lines.push("");
    }
    if (sf.regiszter_megszolitas) {
      lines.push("### Regiszter és megszólítás");
      lines.push(sf.regiszter_megszolitas);
      lines.push("");
    }
    if (sf.cta_stilus) {
      lines.push("### CTA-stílus");
      if (Array.isArray(sf.cta_stilus.minta_cta_k) && sf.cta_stilus.minta_cta_k.length) {
        lines.push("**Minta CTA-k:** " + listToCsv(sf.cta_stilus.minta_cta_k));
        lines.push("");
      }
      if (sf.cta_stilus.elv) {
        lines.push(sf.cta_stilus.elv);
        lines.push("");
      }
    }
    if (Array.isArray(sf.elkotelezodes_technikak) && sf.elkotelezodes_technikak.length) {
      lines.push("### Elköteleződés-építő technikák");
      lines.push(bulletList(sf.elkotelezodes_technikak));
      lines.push("");
    }
    if (sf.irasjelek_vizualis) {
      lines.push("### Írásjelek és vizuális jelölők");
      lines.push(sf.irasjelek_vizualis);
      lines.push("");
    }
    if (sf.blokkszerkezet) {
      lines.push("### Bekezdés- és blokkszerkezet");
      lines.push(sf.blokkszerkezet);
      lines.push("");
    }
  }

  if (Array.isArray(bp.kerulendo_szavak) && bp.kerulendo_szavak.length) {
    lines.push("## KERÜLENDŐ SZAVAK ÉS HANGNEMEK");
    lines.push(bulletList(bp.kerulendo_szavak, { strike: true }));
    lines.push("");
  }

  if (bp.osszegzes) {
    lines.push("## A TE HANGOD, EGYBEN — ÖSSZEGZÉS");
    bp.osszegzes
      .split(/\n\n+/)
      .map((p) => p.replace(/^\s*\*+\s*|\s*\*+\s*$/g, "").trim())
      .filter(Boolean)
      .forEach((p) => {
        lines.push(`_${p}_`);
        lines.push("");
      });
  }

  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
}

/**
 * Convert a saved Webinar Blueprint (`carousel_content` shape) to Markdown.
 * Handles both shapes the app persists:
 *   (a) `carousel_content: [slide, ...]`             (v7 Section Carousel save)
 *   (b) `carousel_content: { diak: [slide, ...] }`   (legacy callbook-funnel parse)
 * Includes: title, saved date, sections, slide headline/bullets/CTA/kérdés,
 * VIZUÁLIS JAVASLAT (visual_suggestion), előadói jegyzet (speaker_notes).
 */
export function webinarBlueprintToMarkdown(bp, name, savedAt) {
  if (!bp || typeof bp !== "object") return `# ${name || "Webinár"}\n\n_Nincs tartalom._\n`;
  const cc = bp.carousel_content;
  const slides = Array.isArray(cc) ? cc : Array.isArray(cc?.diak) ? cc.diak : [];
  const fallbackText = typeof bp.full_text === "string" ? bp.full_text : "";

  const lines = [];
  lines.push(`# ${name || bp.webinar_cim || cc?.webinar_cim || "Webinár"}`);
  if (savedAt) {
    try {
      lines.push(`_Mentve: ${new Date(savedAt).toLocaleString("hu-HU")}_`);
    } catch {
      lines.push(`_Mentve: ${savedAt}_`);
    }
  }
  lines.push("");

  if (!slides.length) {
    if (fallbackText) {
      lines.push(fallbackText);
      return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
    }
    lines.push("_Nincs betöltött webinár tartalom._");
    return lines.join("\n") + "\n";
  }

  // Csoportosítás section_id / section_name alapján, sorrendet megőrizve.
  const groups = [];
  let curr = null;
  for (const slide of slides) {
    const sid = slide?.section_id || "_none";
    if (!curr || curr.sid !== sid) {
      curr = { sid, name: slide?.section_name || "", slides: [] };
      groups.push(curr);
    }
    curr.slides.push(slide);
  }

  groups.forEach((sec) => {
    if (sec.name) {
      lines.push(`## ${sec.name}`);
      lines.push("");
    }
    sec.slides.forEach((slide) => {
      const parts = [];
      if (slide.dia_szam) parts.push(`Slide ${slide.dia_szam}`);
      if (slide.pill) parts.push(slide.pill);
      const header = parts.join(" · ");
      if (header) {
        lines.push(`### ${header}`);
      }
      if (slide.visible_headline) {
        lines.push(`**${slide.visible_headline}**`);
        lines.push("");
      }
      if (Array.isArray(slide.visible_bullets) && slide.visible_bullets.length) {
        slide.visible_bullets.forEach((b) => {
          if (b && String(b).trim()) lines.push(`- ${b}`);
        });
        lines.push("");
      }
      if (slide.visible_question) {
        lines.push(`_${slide.visible_question}_`);
        lines.push("");
      }
      if (slide.visible_cta) {
        lines.push(`**CTA:** ${slide.visible_cta}`);
        lines.push("");
      }
      if (slide.visual_suggestion) {
        lines.push(`> **Vizuális javaslat:** ${slide.visual_suggestion}`);
        lines.push("");
      }
      if (slide.speaker_notes) {
        lines.push(`**Előadói jegyzet:**`);
        lines.push("");
        String(slide.speaker_notes)
          .split(/\n+/)
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((p) => lines.push(`> ${p}`));
        lines.push("");
      }
    });
  });

  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
}

export function downloadMarkdown(filename, content) {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename.endsWith(".md") ? filename : `${filename}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 500);
}
